import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MarketplaceApiService } from '../services/marketplace-api.service';
import { EstadoSede, EstadoSedeTono, tonoEstadoSede } from '../models/marketplace.models';
import { SeoService } from '../../shared/seo/seo.service';
import { seoMarca } from '../seo-marketplace';

@Component({
  selector: 'app-marca-detail',
  standalone: true,
  imports: [CommonModule, RouterLink],
  styles: [`
    :host{ display:block; background:var(--bg,#eef2f8); min-height:100vh;
      --ink:#12263a; --muted:#778798; --line:#e9eef5; --card:#fff; }
    *{ box-sizing:border-box; }
    .shell{ max-width:520px; margin:0 auto; padding-bottom:40px; }

    /* ---- Hero ---- */
    .hero{ position:relative; color:#fff; padding:16px 18px 64px;
      border-radius:0 0 26px 26px; overflow:hidden; }
    .hero::after{ content:''; position:absolute; inset:0; background:radial-gradient(120% 90% at 85% 0%, rgba(255,255,255,.18), transparent 60%); pointer-events:none; z-index:1; }
    .hero .portada{ position:absolute; inset:0; width:100%; height:100%; object-fit:cover; z-index:0; }
    .hero .portada-ov{ position:absolute; inset:0; z-index:0; }
    .hero .nav{ display:flex; align-items:center; justify-content:space-between; position:relative; z-index:1; }
    .circle{ width:40px; height:40px; border-radius:50%; display:grid; place-items:center;
      background:rgba(255,255,255,.22); color:#fff; text-decoration:none; border:0; cursor:pointer;
      font-size:17px; backdrop-filter:blur(4px); }
    .hero .head{ display:flex; gap:14px; align-items:center; margin-top:16px; position:relative; z-index:1; }
    .avatar{ width:66px; height:66px; border-radius:18px; display:grid; place-items:center; flex:none; overflow:hidden;
      background:rgba(255,255,255,.2); color:#fff; font-weight:800; font-size:28px; border:2px solid rgba(255,255,255,.45); }
    .avatar img{ width:100%; height:100%; object-fit:cover; }
    .hero .badges{ display:flex; gap:8px; margin:14px 0 10px; position:relative; z-index:1; }
    .chip{ display:inline-flex; align-items:center; gap:5px; background:rgba(255,255,255,.9); color:var(--ink);
      border-radius:999px; padding:5px 11px; font-size:12.5px; font-weight:700; }
    .chip.rubro{ background:rgba(255,255,255,.18); color:#fff; text-transform:capitalize; }
    .hero h1{ margin:0; font-size:1.55rem; line-height:1.14; font-weight:800; }
    .hero .desc{ margin-top:8px; font-size:13.5px; opacity:.95; position:relative; z-index:1; }

    /* ---- Sheet ---- */
    .sheet{ background:var(--card); margin:-44px 12px 0; border-radius:22px; padding:18px 16px;
      box-shadow:0 12px 34px rgba(15,60,95,.12); position:relative; }
    .h{ font-size:1.02rem; font-weight:800; color:var(--ink); margin:0 0 12px; display:flex;
      align-items:center; justify-content:space-between; }
    .h .meta{ font-size:12.5px; color:var(--muted); font-weight:600; }
    .sec + .sec{ margin-top:22px; }

    /* ---- Sedes ---- */
    .sede{ border:1.5px solid var(--line); border-radius:14px; overflow:hidden; margin-bottom:10px;
      transition:border-color .15s, background .15s; }
    .sede:last-child{ margin-bottom:0; }
    .sede:hover{ border-color:var(--accent); background:color-mix(in srgb, var(--accent) 6%, #fff); }
    .sede-main{ display:flex; align-items:center; gap:12px; padding:13px; text-decoration:none; }
    .sede .info{ flex:1; min-width:0; }
    .sede .dist{ font-size:11px; font-weight:800; text-transform:uppercase; letter-spacing:.5px; color:var(--accent); }
    .sede h3{ margin:2px 0 3px; font-size:.98rem; color:var(--ink); }
    .sede .r{ font-size:12.5px; color:var(--muted); margin:1px 0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .sede .r.row{ display:flex; align-items:center; gap:7px; overflow:visible; white-space:normal; }
    .sede .r.row .txt{ min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

    /* ---- Chip de estado operativo (texto armado por el backend) ---- */
    .est{ flex:none; display:inline-flex; align-items:center; gap:5px; border-radius:999px;
      padding:3px 9px; font-size:11.5px; font-weight:700; line-height:1.5; border:1px solid transparent; }
    .est::before{ content:''; width:7px; height:7px; border-radius:50%; background:currentColor; flex:none; }
    .est-ok{ background:#eefaf1; color:#177a45; border-color:#c6ebd3; }
    .est-busy{ background:#fff6e6; color:#b45309; border-color:#f3ddb2; }
    .est-off{ background:#f1f4f8; color:#7c8794; border-color:#e2e8f0; }
    .sede .arrow{ flex:none; width:30px; height:30px; border-radius:50%; display:grid; place-items:center;
      color:#fff; font-size:15px; background:var(--accent); }
    .sede-actions{ display:flex; gap:8px; padding:0 13px 12px; flex-wrap:wrap; }
    .act{ display:inline-flex; align-items:center; gap:6px; text-decoration:none; border:1.5px solid var(--line);
      border-radius:999px; padding:6px 12px; font-size:12.5px; font-weight:700; color:var(--ink); background:#fff; transition:.15s; }
    .act:hover{ border-color:var(--accent); color:var(--accent); }
    .act.wa{ color:#128c7e; border-color:#bfe6df; }

    /* ---- Redes ---- */
    .libro{ margin:24px 0 8px; text-align:center; }
    .libro a{ color:#48586a; font-weight:700; text-decoration:none; }
    .redes{ display:flex; flex-wrap:wrap; gap:8px; margin-top:12px; position:relative; z-index:1; }
    .red{ display:inline-flex; align-items:center; gap:6px; text-decoration:none; background:rgba(255,255,255,.18);
      color:#fff; border-radius:999px; padding:7px 12px; font-size:12.5px; font-weight:700; transition:.15s; }
    .red:hover{ background:rgba(255,255,255,.3); }

    /* ---- Servicios preview ---- */
    .svc{ display:flex; align-items:center; gap:12px; padding:11px 0; border-bottom:1px solid var(--line); }
    .svc:last-child{ border-bottom:0; }
    .svc .info{ flex:1; min-width:0; }
    .svc h4{ margin:0 0 2px; font-size:.95rem; color:var(--ink); font-weight:700; }
    .svc .d{ font-size:12px; color:var(--muted); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
    .svc .price{ text-align:right; white-space:nowrap; font-weight:800; color:var(--ink); font-size:.98rem; }
    .svc .price small{ color:var(--muted); font-weight:600; }

    .pill{ display:inline-block; background:color-mix(in srgb, var(--accent) 12%, #fff); color:var(--accent);
      border-radius:999px; padding:3px 11px; font-weight:700; font-size:12px; }

    .loading{ text-align:center; color:var(--muted); padding:80px 20px; }

    /* ---- Tablet/desktop: 2 columnas ---- */
    @media (min-width:900px){
      .shell{ max-width:980px; }
      .hero{ border-radius:0 0 30px 30px; padding:16px 24px 68px; }
      .hero .nav, .hero .head, .hero .badges, .hero .desc{ max-width:900px; margin-left:auto; margin-right:auto; }
      .hero h1{ font-size:1.8rem; }
      .sheet{ margin:-48px 16px 0; padding:22px; display:grid; grid-template-columns:1.04fr 1fr;
        gap:24px; align-items:start; }
      .sec + .sec{ margin-top:0; }
    }
    @media (min-width:1200px){
      .shell{ max-width:1100px; }
      .sheet{ grid-template-columns:1fr 1fr; }
    }
  `],
  template: `
  @if (marca) {
    <div class="shell">
      <div class="hero" [class.has-portada]="marca.portadaUrl"
           [style.--accent]="color"
           [style.background]="'linear-gradient(150deg,'+color+' 0%, '+shade+' 100%)'">
        @if (marca.portadaUrl) {
          <img class="portada" [src]="marca.portadaUrl" alt="" loading="lazy" />
          <div class="portada-ov" [style.background]="'linear-gradient(150deg,'+color+'cc 0%, '+shade+'e6 100%)'"></div>
        }
        <div class="nav">
          <a class="circle" routerLink="/marketplace" aria-label="Volver">←</a>
        </div>
        <div class="head">
          <div class="avatar">
            @if (marca.logoUrl) { <img [src]="marca.logoUrl" alt="" loading="lazy" /> }
            @else { {{ marca.nombre.charAt(0) }} }
          </div>
          <div>
            <div class="badges">
              @if (marca.rating) { <span class="chip">★ {{ marca.rating }}</span> }
              @if (marca.rubro) { <span class="chip rubro">{{ marca.rubro }}</span> }
            </div>
            <h1>{{ marca.nombre }}</h1>
          </div>
        </div>
        @if (marca.descripcion) { <div class="desc">{{ marca.descripcion }}</div> }
        @if (hasRedes) {
          <div class="redes">
            @if (marca.redes?.instagram) { <a class="red" [href]="marca.redes.instagram" target="_blank" rel="noopener">📷 Instagram</a> }
            @if (marca.redes?.facebook) { <a class="red" [href]="marca.redes.facebook" target="_blank" rel="noopener">👍 Facebook</a> }
            @if (marca.redes?.tiktok) { <a class="red" [href]="marca.redes.tiktok" target="_blank" rel="noopener">🎵 TikTok</a> }
            @if (marca.redes?.web) { <a class="red" [href]="marca.redes.web" target="_blank" rel="noopener">🌐 Web</a> }
          </div>
        }
      </div>

      <div class="sheet" [style.--accent]="color">
        <!-- Sedes -->
        <div class="sec">
          <div class="h">
            Sedes
            <span class="meta">{{ marca.sedes.length }} sede(s)</span>
          </div>
          @for (s of marca.sedes; track s.id) {
            <div class="sede">
              <a class="sede-main" [routerLink]="['/marketplace', marca.slug, s.slug]" [attr.aria-label]="'Ver servicios de ' + s.nombre">
                <div class="info">
                  @if (s.distrito) { <div class="dist">{{ s.distrito }}</div> }
                  <h3>{{ s.nombre }}</h3>
                  @if (s.direccion) { <div class="r">📍 {{ s.direccion }}</div> }
                  @if (s.horario || tono(s)) {
                    <div class="r row">
                      @if (s.horario) { <span class="txt">🕒 {{ s.horario }}</span> }
                      @if (tono(s); as t) {
                        <span class="est" [ngClass]="'est-' + t" [attr.title]="s.estado?.motivo">{{ s.estado.etiqueta }}</span>
                      }
                    </div>
                  }
                  <div class="r"><span class="pill">{{ s.servicios?.length ?? marca.servicios.length }} servicios</span></div>
                </div>
                <span class="arrow">→</span>
              </a>
              @if (s.whatsapp || s.lat != null && s.lng != null || s.mapsUrl) {
                <div class="sede-actions">
                  @if (s.whatsapp) {
                    <a class="act wa" [href]="waHref(s)" target="_blank" rel="noopener">💬 WhatsApp</a>
                  }
                  @if (s.lat != null && s.lng != null || s.mapsUrl) {
                    <a class="act" [href]="mapsHref(s)" target="_blank" rel="noopener">📍 Ver en Maps</a>
                  }
                </div>
              }
            </div>
          }
        </div>

        <!-- Preview de servicios -->
        @if (marca.servicios?.length) {
          <div class="sec">
            <div class="h">Servicios</div>
            @for (sv of marca.servicios; track sv.id) {
              <div class="svc">
                <div class="info">
                  <h4>{{ sv.nombre }}</h4>
                  @if (sv.descripcion) { <div class="d">{{ sv.descripcion }}</div> }
                  @else if (sv.duracionMin) { <div class="d">⏱ {{ sv.duracionMin }} min</div> }
                </div>
                <div class="price"><small>S/</small> {{ sv.precioBruto }}</div>
              </div>
            }
          </div>
        }
        <!-- Obligatorio a la vista (Código de Protección al Consumidor). SPEC-037. -->
        <p class="libro"><a [routerLink]="['/reclamos', marca.slug]">📕 Libro de Reclamaciones</a></p>
      </div>
    </div>
  } @else {
    <div class="loading">{{ error ? 'No se encontró el negocio.' : 'Cargando…' }}</div>
  }
  `,
})
export class MarcaDetailComponent implements OnInit {
  marca?: any;
  error = false;
  color = '#00A7DB';
  shade = '#0f6ea8';

  constructor(
    private route: ActivatedRoute,
    private api: MarketplaceApiService,
    private cdr: ChangeDetectorRef,
    private seo: SeoService,
  ) {}

  /**
   * Tono del chip de estado de una sede. `null` cuando el backend no mandó `estado`
   * (o llegó sin etiqueta): en ese caso no se pinta chip — jamás se asume "Abierto".
   */
  tono(s: any): EstadoSedeTono | null {
    return tonoEstadoSede(s?.estado as EstadoSede | undefined);
  }

  /** ¿La marca tiene alguna red social? */
  get hasRedes(): boolean {
    const r = this.marca?.redes;
    return !!(r && (r.instagram || r.facebook || r.tiktok || r.web));
  }

  /** Link directo de WhatsApp para una sede (sin mensaje; el detalle arma el mensaje completo). */
  waHref(s: any): string {
    const tel = String(s?.whatsapp ?? '').replace(/[^\d]/g, '');
    return `https://wa.me/${tel}`;
  }

  /** Link a Google Maps: mapsUrl si existe, si no lat/lng, si no la dirección. */
  mapsHref(s: any): string {
    if (s?.mapsUrl) return s.mapsUrl;
    if (s?.lat != null && s?.lng != null) return `https://www.google.com/maps?q=${s.lat},${s.lng}`;
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s?.direccion ?? s?.nombre ?? '')}`;
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((p) => {
      const slug = p.get('marca') ?? '';
      this.api.getMarca(slug).subscribe({
        next: (m) => {
          this.marca = m;
          if (m) {
            this.seo.set(seoMarca(m));
            this.color = MarketplaceApiService.colorDe(m);
            this.shade = this.darken(this.color);
          } else {
            this.error = true;
            // Una URL de negocio inexistente responde 200 (es la SPA): sin noindex, Google
            // la indexaría como página vacía.
            this.seo.set({ titulo: 'Negocio no encontrado', descripcion: 'Este negocio no existe o ya no está publicado en Aura.', path: '/marketplace', noindex: true });
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.error = true;
          this.cdr.detectChanges();
        },
      });
    });
  }

  private darken(hex: string): string {
    const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!m) return hex;
    const f = (h: string) => Math.max(0, Math.round(parseInt(h, 16) * 0.62));
    return `rgb(${f(m[1])},${f(m[2])},${f(m[3])})`;
  }
}
